import { useId } from 'react';
import { getCountry } from '@quds/shared';
import { LocationFields } from '@/components/location';
import { FormField, Input, Textarea, fieldDescribedBy } from '@/components/ui';
import type { FieldErrors, PharmacyInfoValues } from '../validation';

interface PharmacyInfoFieldsProps {
  values: PharmacyInfoValues;
  errors: FieldErrors<PharmacyInfoValues>;
  onChange: (patch: Partial<PharmacyInfoValues>) => void;
  /** Category-neutral label; default keeps Edit Pharmacy modal copy. */
  nameLabel?: string;
  namePlaceholder?: string;
}

/** Master information fields shared by the Add Business wizard and Edit Pharmacy modal. */
export function PharmacyInfoFields({
  values,
  errors,
  onChange,
  nameLabel = 'Pharmacy Name',
  namePlaceholder = 'e.g. Al Shifa Pharmacy',
}: PharmacyInfoFieldsProps) {
  const base = useId();
  const id = (field: keyof PharmacyInfoValues) => `${base}-${field}`;
  const country = getCountry(values.country);
  const described = (field: keyof PharmacyInfoValues, hint?: string) => fieldDescribedBy(id(field), { hint, error: errors[field] });

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <FormField label={nameLabel} htmlFor={id('name')} required error={errors.name} className="sm:col-span-2">
        <Input
          id={id('name')}
          value={values.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder={namePlaceholder}
          invalid={!!errors.name}
          aria-describedby={described('name')}
          maxLength={120}
          autoComplete="organization"
        />
      </FormField>

      <LocationFields
        values={{
          country: values.country,
          administrativeAreaLevel1: values.administrativeAreaLevel1,
          city: values.city,
          area: values.area,
          address: values.address,
        }}
        errors={{
          country: errors.country,
          administrativeAreaLevel1: errors.administrativeAreaLevel1,
          city: errors.city,
          area: errors.area,
          address: errors.address,
        }}
        onChange={onChange}
      />

      <FormField label="Contact Person" htmlFor={id('contactPerson')} required error={errors.contactPerson}>
        <Input
          id={id('contactPerson')}
          value={values.contactPerson}
          onChange={(e) => onChange({ contactPerson: e.target.value })}
          placeholder="Full name"
          invalid={!!errors.contactPerson}
          aria-describedby={described('contactPerson')}
          autoComplete="name"
        />
      </FormField>

      <FormField label="Phone" htmlFor={id('phone')} required error={errors.phone}>
        <Input
          id={id('phone')}
          type="tel"
          value={values.phone}
          onChange={(e) => onChange({ phone: e.target.value })}
          placeholder={country ? `${country.dialCode} …` : '+962 79 123 4567'}
          invalid={!!errors.phone}
          aria-describedby={described('phone')}
          autoComplete="tel"
        />
      </FormField>

      <FormField label="Email" htmlFor={id('email')} required error={errors.email} className="sm:col-span-2">
        <Input
          id={id('email')}
          type="email"
          value={values.email}
          onChange={(e) => onChange({ email: e.target.value })}
          placeholder="contact@pharmacy.com"
          invalid={!!errors.email}
          aria-describedby={described('email')}
          autoComplete="email"
        />
      </FormField>

      <FormField label="Notes" htmlFor={id('notes')} error={errors.notes} hint="Internal notes, visible to Quds staff only." className="sm:col-span-2">
        <Textarea
          id={id('notes')}
          value={values.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
          invalid={!!errors.notes}
          aria-describedby={described('notes', 'hint')}
          maxLength={1000}
        />
      </FormField>
    </div>
  );
}
